import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

// ── TENG UNIFIED SEARCH ────────────────────────────────────────────────────
// ONE search bar site-wide. Native Shopify keyword search answers first; when
// it returns nothing we fall back to the SAME semantic / job-intent endpoint
// the search results page uses (assets/teng-job-intent.js), so a shopper who
// types "what socket for a 2013 Audi A4" gets an answer in the dropdown
// instead of "No results found".
//
// WHY the slug and not the hostname: commerce.storefront_domains is empty, so
// hostname resolution never fires and the API would return HTTP 200 with an
// empty body — indistinguishable from a genuine no-match. See
// memory/reference/shopify.md §1.
//
// Styles are injected inline because this dropdown renders on EVERY page,
// while the results-page section stylesheet does not.
const TENG_AI_API = 'https://simplyfactura-api.fly.dev/api/commerce/storefront/search';
const TENG_AI_STOREFRONT = 'teng-usa';
const TENG_AI_MIN_CHARS = 4;
// Shown the moment native keyword search returns nothing, BEFORE the semantic
// call resolves. Without it the shopper sees "No results found" flash and then
// get replaced by results — reads as a broken search even though it worked.
// Product-voice: state what is happening, never chat. Leads into the
// "Closest matches" header that follows.
const TENG_AI_SEARCHING_COPY = 'No exact match — finding the closest.';

let tengAiStylesInjected = false;
function tengInjectAiStyles() {
  if (tengAiStylesInjected || document.getElementById('teng-ai-suggest-styles')) return;
  tengAiStylesInjected = true;
  const style = document.createElement('style');
  style.id = 'teng-ai-suggest-styles';
  style.textContent = [
    '.teng-ai-suggest{padding:12px 16px 8px}',
    '.teng-ai-suggest__head{font-size:.72rem;letter-spacing:.08em;text-transform:uppercase;opacity:.55;margin-bottom:8px}',
    '.teng-ai-suggest__note{font-size:.875rem;line-height:1.45;margin-bottom:12px}',
    '.teng-ai-suggest__row{display:grid;grid-template-columns:repeat(auto-fill,minmax(116px,1fr));gap:14px}',
    '.teng-ai-suggest__card{display:block;text-decoration:none;color:inherit}',
    '.teng-ai-suggest__img{width:100%;aspect-ratio:1;object-fit:contain;background:#f4f4f4;border-radius:6px}',
    '.teng-ai-suggest__name{font-size:.8125rem;line-height:1.3;margin-top:6px}',
    '.teng-ai-suggest__price{font-size:.8125rem;opacity:.7;margin-top:2px}',
    '.teng-ai-searching{opacity:.6;animation:teng-ai-pulse 1.2s ease-in-out infinite}',
    '@keyframes teng-ai-pulse{0%,100%{opacity:.35}50%{opacity:.75}}',
    '@media (prefers-reduced-motion:reduce){.teng-ai-searching{animation:none}}',
  ].join('');
  document.head.appendChild(style);
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,body,product_type,tag,vendor');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        // Teng unified search: native found nothing — ask the semantic layer.
        if (resultCount === 0) {
          // Swap the copy SYNCHRONOUSLY — the semantic call is ~1s and the
          // shopper must never read "No results found" for a query we are
          // still answering. Restored verbatim if the fallback finds nothing.
          const noResultsEl = predictiveSearchResults.querySelector('.predictive-search-results__no-results');
          const originalCopy = noResultsEl ? noResultsEl.textContent : null;
          if (noResultsEl) {
            tengInjectAiStyles();
            noResultsEl.textContent = TENG_AI_SEARCHING_COPY;
            noResultsEl.classList.add('teng-ai-searching');
          }
          this.#renderTengAiFallback(searchTerm, predictiveSearchResults, abortController, noResultsEl, originalCopy);
        }
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Teng unified search fallback. Called ONLY when native keyword search
   * returned zero items. Renders semantic matches + any job-intent note into
   * the same dropdown panel.
   *
   * Fails silently by design — an API error must never degrade native search.
   * All text via textContent: no HTML injection surface.
   *
   * @param {string} searchTerm - the shopper's raw query
   * @param {HTMLElement} container - the predictive-search results panel
   * @param {AbortController} abortController - shared with the native fetch
   */
  async #renderTengAiFallback(searchTerm, container, abortController, noResultsEl, originalCopy) {
    // Put the theme's own copy back exactly as it was — anything we cannot
    // answer must look like an ordinary empty result, not a stuck spinner.
    const restore = () => {
      if (!noResultsEl) return;
      noResultsEl.classList.remove('teng-ai-searching');
      if (originalCopy != null) noResultsEl.textContent = originalCopy;
    };

    const term = (searchTerm || '').trim();
    if (term.length < TENG_AI_MIN_CHARS) { restore(); return; }

    try {
      const url =
        `${TENG_AI_API}?storefront=${encodeURIComponent(TENG_AI_STOREFRONT)}&q=${encodeURIComponent(term)}`;
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        signal: abortController.signal,
      });
      if (!response.ok) { restore(); return; }

      const data = await response.json();
      // Aborted means the shopper typed on; a newer request owns the DOM now.
      if (abortController.signal.aborted) return;
      // Stale answer for a query that has moved on — drop it, do not restore.
      if ((this.refs.searchInput?.value || '').trim() !== term) return;

      const results = Array.isArray(data?.results) ? data.results.filter((r) => r && r.handle) : [];
      const message = data?.prompt || data?.note || null;
      if (!results.length && !message) { restore(); return; }

      tengInjectAiStyles();

      const wrap = document.createElement('div');
      wrap.className = 'teng-ai-suggest';

      const head = document.createElement('div');
      head.className = 'teng-ai-suggest__head';
      head.textContent = results.length ? 'Closest matches' : 'Suggestion';
      wrap.appendChild(head);

      if (message) {
        const note = document.createElement('div');
        note.className = 'teng-ai-suggest__note';
        note.textContent = message;
        wrap.appendChild(note);
      }

      if (results.length) {
        const row = document.createElement('div');
        row.className = 'teng-ai-suggest__row';
        for (const result of results.slice(0, 6)) {
          const card = document.createElement('a');
          card.className = 'teng-ai-suggest__card';
          card.href = `/products/${encodeURIComponent(result.handle)}`;
          if (result.image) {
            const img = document.createElement('img');
            img.className = 'teng-ai-suggest__img';
            img.src = result.image;
            img.alt = result.title || '';
            img.loading = 'lazy';
            card.appendChild(img);
          }
          const name = document.createElement('div');
          name.className = 'teng-ai-suggest__name';
          name.textContent = result.title || result.sku || '';
          card.appendChild(name);
          if (result.price) {
            const price = document.createElement('div');
            price.className = 'teng-ai-suggest__price';
            price.textContent = `${result.price}`;
            card.appendChild(price);
          }
          row.appendChild(card);
        }
        wrap.appendChild(row);
      }

      // Clear the theme's "No results found" copy — we DO have an answer.
      // The class is __no-results (NOT __empty — that selector matched nothing
      // and left the contradiction "Closest matches … No results found" on
      // screen). The visually-hidden role=status node is announced to screen
      // readers, so correct it too rather than leaving it lying.
      container.querySelector('.predictive-search-results__no-results')?.remove();
      const srStatus = container.querySelector('[role="status"]');
      if (srStatus) srStatus.textContent = `${results.length} suggested result${results.length === 1 ? '' : 's'}`;

      container.prepend(wrap);
    } catch {
      restore(); // silent — native results stand alone
    }
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
